<template>
  <div v-if="formData">
    <CRow class="title pb-2">
      <CCol col="auto pl-0">
        <h2>
          {{ formData.property.name }}
        </h2>
      </CCol>
      <CCol col="auto pr-0">
        <div v-if="editPostingData">
          <CButton
            class="edit-button"
            variant="outline"
            @click="showEdit = true"
          >
            Update Information
          </CButton>
        </div>
        <div v-else>
          <RChangeRequestButton :before-open="setContactSupportFlag" />
        </div>
      </CCol>
    </CRow>
    <RSpinnerOverlay
      :loading="loading"
      :opacity="0.7"
    >
      <CRow class="pt-2">
        <CCol
          md="12"
          lg="6"
          class="mb-4"
        >
          <CCard>
            <CCardHeader>
              Custom Titles
            </CCardHeader>
            <CCardBody>
              <div>
                <label>Behavior</label>
                <CAlert color="info">
                  {{ parseTitleSetting( formData.title_setting ) }}
                </CAlert>
              </div>
              <PostingDetailMultiField
                :field="formData.title"
                label="Titles"
                inline
              />
            </CCardBody>
          </CCard>
          <CCard>
            <CCardHeader>
              Ad Body
            </CCardHeader>
            <CCardBody>
              <PostingDetailMultiField
                :field="formData.romance_text"
                label="Romance Text"
              />
            </CCardBody>
          </CCard>

          <CCard>
            <CCardHeader>
              Banners & Specials
              <CIcon
                v-c-tooltip="{
                  content: 'If specials/headers/footers/disclaimers have multiple entries, PostEngine will cycle through them, using only one for each post.',
                  boundaries: 'window'
                }"
                name="faCircleInfo"
                class="info-icon"
              />
            </CCardHeader>
            <CCardBody>
              <PostingDetailMultiField
                :field="formData.specials"
                label="Specials"
              />
              <PostingDetailMultiField
                :field="formData.header"
                label="Header(s)"
              />
              <PostingDetailMultiField
                :field="formData.footer"
                label="Footer(s)"
              />
              <PostingDetailMultiField
                :field="formData.disclaimer"
                label="Disclaimer(s)"
              />
            </CCardBody>
          </CCard>

          <CCard>
            <CCardHeader>
              Location
            </CCardHeader>
            <CCardBody>
              <div class="d-flex justify-content-between">
                <label>Region</label>
              </div>
              <p class="capitalize region-background">
                {{ formData.major_region ? getRegionById(formData.major_region).name : '(none)' }}
              </p>
              <div class="d-flex justify-content-between">
                <label>Sub Region</label>
              </div>
              <p class="capitalize region-background">
                {{ formData.sub_region ? getSubRegionById(formData.sub_region).name : '(none)' }}
              </p>
              <div class="d-flex justify-content-between">
                <PostingDetailMultiField
                  :field="formData.location"
                  label="Location(s)"
                  inline
                />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol
          md="12"
          lg="6"
          class="mb-4"
        >
          <CCard>
            <CCardHeader>
              Amenities
            </CCardHeader>
            <CCardBody>
              <PostingDetailMultiField
                :field="formData.amenities_property"
                label="Property Amenities"
                inline
              />
              <PostingDetailMultiField
                :field="formData.amenities_unit"
                label="Unit Amenities"
                inline
              />
              <PostingDetailAmenities :property="formData" />
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </RSpinnerOverlay>
    <PostingDetailEdit
      :show.sync="showEdit"
      :property="formData"
      @update:property="fetchProperty"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import { RChangeRequestButton } from '@/components/buttons'
import { RSpinnerOverlay } from '@/components/spinners'
import { CraigslistAPI, RooofAccountAPI } from '@/services/api/resources'

import PostingDetailAmenities from './_components/PostingDetailAmenities'
import PostingDetailEdit from './_components/PostingDetailEdit'
import PostingDetailMultiField from './_components/PostingDetailMultiField'

export default {
  name: 'PostingDetail',
  components: {
    RChangeRequestButton,
    RSpinnerOverlay,
    PostingDetailMultiField,
    PostingDetailAmenities,
    PostingDetailEdit
  },
  data () {
    return {
      loading: false,
      permissions: [],
      peGroups: [],
      editPostingData: false,
      showEdit: false,
      formData: null,
      displayInline: true
    }
  },
  computed: {
    ...mapGetters({
      account: 'auth/account',
      getRegionById: 'craigslist/getRegionById',
      getSubRegionById: 'craigslist/getSubRegionById'
    })
  },
  created () {
    this.fetchProperty()
  },
  methods: {
    /**
     * Fetch property
     *
     */
    async fetchProperty () {
      const id = this.$route.params.pid
      this.loading = true
      const clProperty = await CraigslistAPI.properties.retrieve({ id })
      if (clProperty) {
        this.formData = clProperty.data
        this.peGroups = clProperty.data.property.postengine_groups
      }
      this.fetchPermissions()
      this.loading = false
    },
    /**
     * Fetch user edit permissions
     * There is property level and postengine group level permissions a user can have.
     * Staff are able to edit without explicit permissions.
     *
     */
    async fetchPermissions () {
      const response = await RooofAccountAPI.users.permissions({ id: this.account.id })
      this.permissions = response.data

      const propertyPermissions = this.permissions.find(permission => {
        return permission.object_pk === this.$route.params.pid &&
        permission.content_type.model === 'craigslistproperty' &&
        permission.permission.codename === 'edit_posting_data'
      })

      // check if user has permission to edit for a postengine group the property belongs to
      const groupPermissions = this.permissions.find(permission => {
        return this.peGroups.includes(parseInt(permission.object_pk)) &&
        permission.content_type.model === 'postenginegroup' &&
        permission.permission.codename === 'edit_posting_data'
      })

      if (propertyPermissions || groupPermissions || this.$store.getters['auth/isStaff']) {
        this.editPostingData = true
      }
    },
    /**
     * Get custom title setting choices to be displayed.
     *
     * @return {Array}
     */
    parseTitleSetting (setting) {
      switch (setting) {
        case 'no_titles':
          return 'Let PostEngine auto-generate titles'
        case 'alternate_titles':
          return 'Randomly pick between stored and auto-generated titles'
        case 'stored_only':
          return 'Randomly pick from stored titles only'
        case 'user_choose':
          return 'Let me pick a custom title every time'
        default:
          return 'Unknown'
      }
    },
    /**
     * Sets the "My company/property information is incorrect" checkbox
     * in the Contact Support modal.
     */
    setContactSupportFlag () {
      this.$store.commit('support/set', ['incorrect_company_property_info', true])
    }
  }
}
</script>

<style lang="scss" scoped>
.title {
  margin: auto;
  justify-content: space-between;
  align-items: center;
}

.edit-button {
  background-color: $gray-50;
}

.edit-button:hover {
  background-color: $gray-100;
}

.card {
  .card-header {
    font-weight: 500;
    font-size: 1.2rem;
    display: flex;
    align-items: center;

    .info-icon {
      margin-left: 4px;
      color: $gray-50;
    }
  }
  ::v-deep .card-body {
    label {
      font-size: 1rem;
      font-weight: bold;
    }
    .capitalize {
      text-transform: capitalize;
    }
    .region-background {
      background-color: #ebf2fe;
      padding: 8px;
      border-radius: 4px;
      display: inline-block;
    }

    .posting-detail-field:last-of-type p {
      margin-bottom: 0;
    }
  }
}
</style>
